import { useEffect, useState } from 'react'
import { api, type AdminRow, type AlertSettings, type InviteRow, type SignupInfo } from './api'
import { formatTime, relativeTime } from './ui'

/** Operator accounts, invites, and the signup policy for the deployment. */
export default function TeamView(): React.JSX.Element {
  const [admins, setAdmins] = useState<AdminRow[]>([])
  const [invites, setInvites] = useState<InviteRow[]>([])
  const [info, setInfo] = useState<SignupInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [fresh, setFresh] = useState<{ code: string; expiresInHours: number } | null>(null)

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteNote, setInviteNote] = useState('')
  const [showInvite, setShowInvite] = useState(false)

  const [showPassword, setShowPassword] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [passwordNote, setPasswordNote] = useState<string | null>(null)

  const refresh = async (): Promise<void> => {
    try {
      const [a, i, s] = await Promise.all([
        api.listAdmins(),
        api.listInvites(),
        api.signupInfo()
      ])
      setAdmins(a.admins)
      setInvites(i.invites)
      setInfo(s)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  useEffect(() => {
    void refresh()
  }, [])

  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    try {
      await fn()
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  const createInvite = async (): Promise<void> => {
    try {
      const result = await api.createInvite(inviteEmail, inviteNote)
      setFresh(result)
      setInviteEmail('')
      setInviteNote('')
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  const changePassword = async (): Promise<void> => {
    setPasswordNote(null)
    try {
      await api.changePassword(currentPassword, newPassword)
      setPasswordNote('Password changed. Your other sessions were signed out.')
      setCurrentPassword('')
      setNewPassword('')
      setShowPassword(false)
    } catch (err) {
      setPasswordNote(err instanceof Error ? err.message : String(err))
    }
  }

  const inviteStatus = (i: InviteRow): string => {
    if (i.usedAt) return `used ${relativeTime(i.usedAt)}`
    if (i.revoked) return 'revoked'
    if (i.expired) return 'expired'
    return `expires ${relativeTime(i.expiresAt)}`
  }

  return (
    <div className="stack">
      {error && <div className="banner error">{error}</div>}

      <section className="card">
        <h2>Signup policy</h2>
        {info?.openSignup ? (
          <div className="banner error">
            <strong>Open signup is on.</strong> Anyone who can reach this console
            can create an account and control your streaming fleet. Leave this off
            unless the console is on a trusted private network.
          </div>
        ) : (
          <p className="hint">
            Signup requires an invite. This is the recommended setting — the
            console can start and stop live broadcasts.
          </p>
        )}
        <label className={`check ${info?.openSignup ? 'on' : ''}`}>
          <input
            type="checkbox"
            checked={info?.openSignup ?? false}
            onChange={(e) => void act(() => api.setOpenSignup(e.target.checked))}
          />
          <span>Allow anyone to sign up without an invite</span>
        </label>
      </section>

      <section className="card">
        <div className="card-head">
          <h2>Operators</h2>
          <button onClick={() => setShowPassword((v) => !v)}>
            {showPassword ? 'Close' : 'Change my password'}
          </button>
        </div>

        {showPassword && (
          <div className="stack-tight">
            <input
              type="password"
              placeholder="Current password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
            <input
              type="password"
              placeholder="New password — at least 10 characters"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <div className="inline">
              <button className="primary" onClick={() => void changePassword()}>
                Change password
              </button>
            </div>
          </div>
        )}
        {passwordNote && <div className="banner ok">{passwordNote}</div>}

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Last sign-in</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.id} className={a.disabled ? 'muted' : undefined}>
                  <td>
                    <strong>{a.email}</strong>
                    {a.isYou && <span className="badge">you</span>}
                    <div className="sub">joined {formatTime(a.createdAt)}</div>
                  </td>
                  <td className="sub">{relativeTime(a.lastLoginAt)}</td>
                  <td className="sub">{a.disabled ? 'disabled' : 'active'}</td>
                  <td>
                    {!a.isYou && (
                      <button
                        className={a.disabled ? '' : 'danger'}
                        onClick={() => void act(() => api.setAdminDisabled(a.id, !a.disabled))}
                      >
                        {a.disabled ? 'Enable' : 'Disable'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="hint">
          Accounts are disabled rather than deleted, so the config revisions and
          commands they authored keep their attribution. Disabling also signs the
          account out everywhere immediately.
        </p>
      </section>

      <section className="card">
        <div className="card-head">
          <h2>Invites</h2>
          <button onClick={() => setShowInvite((v) => !v)}>
            {showInvite ? 'Close' : 'Invite someone'}
          </button>
        </div>

        {showInvite && (
          <div className="stack-tight">
            <input
              placeholder="Email to lock this invite to (optional)"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
            <input
              placeholder="Note — e.g. weekend operator"
              value={inviteNote}
              onChange={(e) => setInviteNote(e.target.value)}
            />
            <div className="inline">
              <button className="primary" onClick={() => void createInvite()}>
                Generate invite
              </button>
            </div>
            <p className="hint">
              Binding an invite to an email means a forwarded code cannot be
              redeemed by anyone else.
            </p>
          </div>
        )}

        {fresh && (
          <div className="banner ok">
            <div>Share this code — it works once, within {fresh.expiresInHours} hours:</div>
            <code className="code-big">{fresh.code}</code>
          </div>
        )}

        {invites.length === 0 ? (
          <p className="hint">No invites yet.</p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>For</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {invites.map((i) => {
                  const dead = Boolean(i.usedAt) || i.revoked || i.expired
                  return (
                    <tr key={i.id} className={dead ? 'muted' : undefined}>
                      <td>
                        <code>{i.code}</code>
                        <div className="sub">by {i.createdByEmail}</div>
                      </td>
                      <td className="sub">
                        {i.email ?? 'anyone'}
                        {i.note && <div>{i.note}</div>}
                      </td>
                      <td className="sub">{inviteStatus(i)}</td>
                      <td>
                        {!dead && (
                          <button
                            className="danger"
                            onClick={() => void act(() => api.revokeInvite(i.id))}
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AlertsCard onError={setError} />
    </div>
  )
}

/**
 * Fleet-alert channels. The device alerts on its own stream from the box, but a
 * power cut or dead uplink takes that notifier down with it — only the control
 * plane can page someone about a box that went silent, and this is where the
 * page goes.
 */
function AlertsCard({ onError }: { onError: (e: string | null) => void }): React.JSX.Element {
  const [settings, setSettings] = useState<AlertSettings | null>(null)
  const [token, setToken] = useState('')
  const [chat, setChat] = useState('')
  const [webhook, setWebhook] = useState('')
  const [afterSec, setAfterSec] = useState(180)
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void api
      .getAlertSettings()
      .then((s) => {
        setSettings(s)
        setChat(s.telegramChat)
        setWebhook(s.webhookUrl)
        setAfterSec(s.offlineAfterSec)
      })
      .catch((err) => onError(err instanceof Error ? err.message : String(err)))
  }, [onError])

  const save = async (): Promise<void> => {
    setBusy(true)
    setNote(null)
    try {
      const r = await api.setAlertSettings({
        // Blank keeps the stored token; the server never echoes it back whole.
        telegramToken: token.trim(),
        telegramChat: chat,
        webhookUrl: webhook,
        offlineAfterSec: afterSec
      })
      setToken('')
      setSettings(await api.getAlertSettings())
      setNote(`Saved. Offline alert after ${r.offlineAfterSec}s without a heartbeat.`)
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const test = async (): Promise<void> => {
    setBusy(true)
    setNote(null)
    try {
      const { results } = await api.testAlert()
      setNote(
        results
          .map((r) => `${r.channel}: ${r.ok ? 'delivered ✓' : `failed — ${r.error}`}`)
          .join(' · ')
      )
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2>Fleet alerts</h2>
      </div>
      <p className="sub">
        The server pages these channels when a device stops heartbeating — the one failure the
        device cannot report itself — and again when it comes back. Outages also land in the
        device&apos;s own timeline.
      </p>
      <div className="row">
        <label className="field">
          <span>
            Telegram bot token{' '}
            {settings?.telegramConfigured && (
              <em className="sub">(saved …{settings.telegramTokenTail} — blank keeps it)</em>
            )}
          </span>
          <input
            type="password"
            value={token}
            placeholder={settings?.telegramConfigured ? 'unchanged' : '123456:ABC-…'}
            autoComplete="off"
            onChange={(e) => setToken(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Telegram chat id</span>
          <input value={chat} placeholder="-100123456789" onChange={(e) => setChat(e.target.value)} />
        </label>
      </div>
      <div className="row">
        <label className="field">
          <span>Webhook URL (JSON POST)</span>
          <input
            value={webhook}
            placeholder="https://…"
            onChange={(e) => setWebhook(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Offline after (seconds, min 120)</span>
          <input
            type="number"
            min={120}
            value={afterSec}
            onChange={(e) => setAfterSec(Number(e.target.value) || 180)}
          />
        </label>
      </div>
      <div className="row">
        <button className="primary" disabled={busy} onClick={() => void save()}>
          Save
        </button>
        <button disabled={busy} onClick={() => void test()}>
          Send test alert
        </button>
      </div>
      {note && <div className="banner ok">{note}</div>}
    </section>
  )
}
